import { useEffect, useRef, useState } from "react";
import AWS from "aws-sdk";
import intlTelInput from "intl-tel-input/intlTelInputWithUtils";
import "intl-tel-input/dist/css/intlTelInput.css";
import { useAuth } from "../../../contexts/AuthContext";
import { UUIDv4 } from "../../../support";
import "./ProfilePage.css";

const MAX_CV_SIZE = 5 * 1024 * 1024;

const levelOptions = [
    "Undergraduate, first year",
    "Undergraduate, second year",
    "Undergraduate, third year",
    "Undergraduate, final year",
    "Postgraduate taught",
    "Postgraduate research",
    "Graduated",
];

const fieldOptions = [
    "Finance and economics",
    "Engineering",
    "Technology and data",
    "Consulting",
    "Law and public policy",
    "Healthcare and science",
    "Marketing and media",
    "Other",
];

const tuitionFeeSourceOptions = [
    "Self-funded",
    "University-funded",
    "Thai government scholarship",
    "UK government scholarship",
    "Thai industry/commerce",
    "UK industry/commerce",
    "Thai academia",
    "UK academia",
    "Others",
];
const genderOptions = ["Male", "Female", "Other", "Prefer not to say"];
const furtherEducationOptions = ["Yes", "No"];
const titleOptions = ["Mr", "Mrs", "Miss", "Ms", "Mx", "Sir", "Dame", "Dr", "Cllr", "Lady", "Lord"];
const courseYearOptions = Array.from({length: 31}, (_, index) => String(new Date().getFullYear() - 20 + index));

function initials(name) {
    return name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "SS";
}

function dateInputValue(value) {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const oldFormat = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    return oldFormat ? `${oldFormat[3]}-${oldFormat[2]}-${oldFormat[1]}` : "";
}

function splitName(fullName) {
    const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
    return {firstName: parts.shift() || "", lastName: parts.join(" ")};
}

function formatUploadDate(value) {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB");
}

function ProfilePage({setShowCover}) {
    const { getProfile, setProfile } = useAuth();
    const [title, setTitle] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [nickname, setNickname] = useState("");
    const [dateOfBirth, setDateOfBirth] = useState("");
    const [university, setUniversity] = useState("");
    const [level, setLevel] = useState(levelOptions[3]);
    const [course, setCourse] = useState("");
    const [field, setField] = useState(fieldOptions[0]);
    const [gender, setGender] = useState("");
    const [address, setAddress] = useState("");
    const [courseStartYear, setCourseStartYear] = useState("");
    const [courseEndYear, setCourseEndYear] = useState("");
    const [gpa, setGpa] = useState("");
    const [furtherEducation, setFurtherEducation] = useState("No");
    const [lineId, setLineId] = useState("");
    const [countryCode, setCountryCode] = useState("44");
    const [phoneNumber, setPhoneNumber] = useState("");
    const phoneInputRef = useRef(null);
    const phoneInputInstanceRef = useRef(null);
    const [tuitionFeeSources, setTuitionFeeSources] = useState([]);
    const [profileData, setProfileData] = useState({});
    const [discovered, setDiscovered] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [authenticationMessage, setAuthenticationMessage] = useState("");
    const [cvFile, setCvFile] = useState();
    const [cvLink, setCvLink] = useState("");
    const [cvUploadedAt, setCvUploadedAt] = useState("");
    const isLocalPreview = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

    useEffect(() => {
        let active = true;
        if (isLocalPreview) {
            setLoading(false);
            return () => { active = false; };
        }
        setShowCover(true);
        getProfile(true)
            .then(async (response) => {
                const result = await response.json();
                if (!active) return;
                if (response.status === 200) {
                    const profile = result.data || {};
                    setProfileData(profile);
                    setCvLink(profile.cvLink || "");
                    setCvUploadedAt(profile.cvUploadedAt || "");
                    setTitle(profile.title || "");
                    const parsedName = splitName(profile.fullName);
                    setFirstName(parsedName.firstName);
                    setLastName(parsedName.lastName);
                    setNickname(profile.nickname || "");
                    setDateOfBirth(dateInputValue(profile.birthdate));
                    setUniversity(profile.university || "");
                    setLevel(profile.level || levelOptions[3]);
                    setCourse(profile.courseName || "");
                    setField(profile.courseArea || fieldOptions[0]);
                    setGender(profile.gender || "");
                    setAddress(profile.address || "");
                    setCourseStartYear(profile.startDate || "");
                    setCourseEndYear(profile.endDate || "");
                    setGpa(profile.gpa || "");
                    setFurtherEducation(profile.furtherEducation || "No");
                    setLineId(profile.lineID || "");
                    const phoneParts = (profile.phoneNumber || "").split("//");
                    setCountryCode(phoneParts[0] || "44");
                    setPhoneNumber(phoneParts[1] || "");
                    setTuitionFeeSources(profile.tuitionFeeSources || []);
                    setDiscovered(Boolean(profile.companyConnect));
                } else if (result.data?.reason === "Invalid Token" || response.status === 403) {
                    setAuthenticationMessage("Your session has expired or you are not signed in.");
                } else if (response.status !== 404) {
                    setError(result.data?.reason || "Unable to load your profile.");
                }
            })
            .catch(() => setError("Unable to load your profile. Please try again."))
            .finally(() => {
                if (active) {
                    setLoading(false);
                    setShowCover(false);
                }
            });
        return () => { active = false; };
        // AuthContext exposes the same API function for the lifetime of this page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!phoneInputRef.current || phoneInputInstanceRef.current) return undefined;

        const initialCountry = countryCode.replace("+", "") === "44" ? "gb" : undefined;
        const inputInstance = intlTelInput(phoneInputRef.current, {
            initialCountry,
            separateDialCode: true,
            nationalMode: true,
            loadUtils: () => import("intl-tel-input/utils"),
        });
        phoneInputInstanceRef.current = inputInstance;

        const handleCountryChange = () => {
            setCountryCode(inputInstance.getSelectedCountry()?.dialCode || "44");
        };
        phoneInputRef.current.addEventListener("countrychange", handleCountryChange);

        return () => {
            phoneInputRef.current?.removeEventListener("countrychange", handleCountryChange);
            inputInstance.destroy();
            phoneInputInstanceRef.current = null;
        };
        // The phone input is initialized once when the form is rendered.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    async function saveProfile(event) {
        event.preventDefault();
        if (saving) return;
        if (isLocalPreview) return;
        if (!firstName.trim() || !lastName.trim() || !university.trim() || !course.trim()) {
            setError("Please complete your name, university, and course.");
            return;
        }
        if (!cvFile && !cvLink) {
            setError("Please upload your CV before continuing.");
            return;
        }

        setSaving(true);
        setError("");
        setShowCover(true);
        try {
            let savedCvLink = cvLink;
            let savedCvUploadedAt = cvUploadedAt;
            if (cvFile) {
                const bucket = "cfas-user-cv-bucket";
                const region = "eu-west-2";
                AWS.config.update({
                    region,
                    credentials: new AWS.CognitoIdentityCredentials({
                        IdentityPoolId: "eu-west-2:67102926-3c2f-4e94-b91a-8058ee31d8b8",
                    }),
                });
                const key = encodeURI(UUIDv4() + cvFile.name.replace(/[^A-Za-z0-9.]/g, ""));
                await new AWS.S3.ManagedUpload({
                    params: {Bucket: bucket, Key: `cv/${key}`, Body: cvFile},
                }).promise();
                savedCvLink = `https://${bucket}.s3.${region}.amazonaws.com/cv/${key}`;
                savedCvUploadedAt = new Date().toISOString();
            }

            const response = await setProfile({
                ...profileData,
                fullName: `${firstName.trim()} ${lastName.trim()}`.trim(),
                title,
                nickname: nickname.trim(),
                birthdate: dateOfBirth,
                year: level,
                courseArea: field,
                level,
                courseName: course.trim(),
                gender,
                address: address.trim(),
                startDate: courseStartYear,
                endDate: courseEndYear,
                gpa: gpa.trim(),
                furtherEducation,
                lineID: lineId.trim(),
                phoneNumber: `${countryCode.replace("+", "")}//${phoneNumber.trim()}`,
                companyConnect: discovered,
                university: university.trim(),
                tuitionFeeSources,
                cvLink: savedCvLink,
                cvUploadedAt: savedCvUploadedAt,
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.data?.reason || "Unable to save your profile.");
            window.alert("Profile saved");
            window.location = "/cfas/hub/home";
        } catch (saveError) {
            setError(saveError.message || "Unable to save your profile. Please try again.");
            setSaving(false);
            setShowCover(false);
        }

    }

    function chooseCv(event) {
        const file = event.target.files[0];
        if (!file) return;
        if (file.size > MAX_CV_SIZE) {
            setError("That file is over 5 MB. Please choose a smaller PDF or Word file.");
            event.target.value = "";
            return;
        }
        setError("");
        setCvFile(file);
        setCvUploadedAt(new Date().toISOString());
    }

    return (
        <div className="profile-page">
            {/* Temporarily disabled until the sign-in page is connected.
            {authenticationMessage && (
                <div className="profile-modal-backdrop" role="presentation">
                    <section className="profile-modal" role="alertdialog" aria-modal="true" aria-labelledby="authentication-title">
                        <div className="profile-modal-icon">!</div>
                        <h2 id="authentication-title">Authentication required</h2>
                        <p>{authenticationMessage}</p>
                        <button className="profile-button primary" type="button" onClick={() => { window.location = "/cfas/hub/sign-in"; }}>
                            Go to sign in
                        </button>
                    </section>
                </div>
            )}
            */}
            <main className="profile-content">
                {loading ? <p className="profile-muted">Loading your profile...</p> : (
                    <div className="profile-stack">
                        <div className="profile-stepper" aria-label="Setup progress">
                            <span className="profile-step complete">1. ACCOUNT</span>
                            <span className="profile-step current">2. PROFILE AND CV</span>
                        </div>
                        <div className="profile-heading">
                            <h1>Step 2 of 2: tell us about you</h1>
                            <p>Employers see this with your CV.</p>
                        </div>
                        {error && <div className="profile-error" role="alert">{error}</div>}
                        <form className="profile-card" onSubmit={saveProfile}>
                            <div className="profile-section-card">
                                <h2>Personal Information</h2>
                                <div className="profile-field">
                                    <label htmlFor="profile-title">Title</label>
                                    <select id="profile-title" value={title} onChange={(event) => setTitle(event.target.value)}>
                                        <option value="">Select title</option>
                                        {titleOptions.map((option) => <option key={option}>{option}</option>)}
                                    </select>
                                </div>
                                <div className="profile-grid">
                                    <div className="profile-field"><label htmlFor="profile-first-name">First name</label><input id="profile-first-name" value={firstName} onChange={(event) => setFirstName(event.target.value)} autoComplete="given-name" /></div>
                                    <div className="profile-field"><label htmlFor="profile-last-name">Last name</label><input id="profile-last-name" value={lastName} onChange={(event) => setLastName(event.target.value)} autoComplete="family-name" /></div>
                                    <div className="profile-field"><label htmlFor="profile-nickname">Nickname</label><input id="profile-nickname" value={nickname} onChange={(event) => setNickname(event.target.value)} autoComplete="nickname" /></div>
                                    <div className="profile-field"><label htmlFor="profile-date-of-birth">Date of birth</label><input id="profile-date-of-birth" type="date" value={dateOfBirth} onChange={(event) => setDateOfBirth(event.target.value)} /></div>
                                    <div className="profile-field"><label htmlFor="profile-gender">Gender</label><select id="profile-gender" value={gender} onChange={(event) => setGender(event.target.value)}><option value="">Select gender</option>{genderOptions.map((option) => <option key={option}>{option}</option>)}</select></div>
                                    <div className="profile-field"><label htmlFor="profile-line-id">Line ID</label><input id="profile-line-id" value={lineId} onChange={(event) => setLineId(event.target.value)} /></div>
                                </div>
                                <div className="profile-field"><label htmlFor="profile-address">Address</label><input id="profile-address" value={address} onChange={(event) => setAddress(event.target.value)} autoComplete="street-address" /></div>
                                <div className="profile-grid">
                                    <div className="profile-field profile-phone-field"><label htmlFor="profile-phone-number">Phone number</label><input id="profile-phone-number" ref={phoneInputRef} value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} autoComplete="tel" inputMode="tel" placeholder="Phone number" /></div>
                                </div>
                            </div>
                            <div className="profile-section-card">
                                <h2>Education Information</h2>
                                <div className="profile-grid">
                                <div className="profile-field"><label htmlFor="profile-university">University</label><input id="profile-university" value={university} onChange={(event) => setUniversity(event.target.value)} /></div>
                                <div className="profile-field"><label htmlFor="profile-level">Level and year</label><select id="profile-level" value={level} onChange={(event) => setLevel(event.target.value)}>{levelOptions.map((option) => <option key={option}>{option}</option>)}</select></div>
                                <div className="profile-field"><label htmlFor="profile-course">Course</label><input id="profile-course" value={course} onChange={(event) => setCourse(event.target.value)} /></div>
                                <div className="profile-field"><label htmlFor="profile-start-year">Starting year for your course</label><select id="profile-start-year" value={courseStartYear} onChange={(event) => setCourseStartYear(event.target.value)}><option value="">Select starting year</option>{courseYearOptions.map((option) => <option key={option}>{option}</option>)}</select></div>
                                <div className="profile-field"><label htmlFor="profile-end-year">Expected graduation year</label><select id="profile-end-year" value={courseEndYear} onChange={(event) => setCourseEndYear(event.target.value)}><option value="">Select graduation year</option>{courseYearOptions.map((option) => <option key={option}>{option}</option>)}</select></div>
                                <div className="profile-field"><label htmlFor="profile-gpa">GPA <span className="profile-optional">(optional)</span></label><input id="profile-gpa" value={gpa} onChange={(event) => setGpa(event.target.value)} inputMode="decimal" /></div>
                                <div className="profile-field"><label htmlFor="profile-further-education">Are you considering pursuing further Education?</label><select id="profile-further-education" value={furtherEducation} onChange={(event) => setFurtherEducation(event.target.value)}>{furtherEducationOptions.map((option) => <option key={option}>{option}</option>)}</select></div>
                                </div>
                                <div className="profile-field"><label htmlFor="profile-field">Field you want to work in</label><select id="profile-field" value={field} onChange={(event) => setField(event.target.value)}>{fieldOptions.map((option) => <option key={option}>{option}</option>)}</select></div>
                                <div className="profile-field">
                                    <label htmlFor="profile-tuition-fees">Source of tuition fees</label><select id="profile-tuition-fees" value={tuitionFeeSources[0] || ""} onChange={(event) => setTuitionFeeSources(event.target.value ? [event.target.value] : [])}><option value="">Select source</option>{tuitionFeeSourceOptions.map((option) => <option key={option}>{option}</option>)}</select>
                                    <span className="profile-field-help">Select all that apply, including partial scholarships</span>
                                </div>
                            </div>
                            <div className="profile-section-card">
                                <h2>CV</h2>
                                <div className={`profile-upload${cvFile || cvLink ? " complete" : ""}`}>
                                    <strong>{cvFile ? "CV uploaded" : (cvLink ? "CV uploaded" : "Upload your CV")}</strong>
                                    <span className="profile-muted">{cvFile ? `${cvFile.name} · ${formatUploadDate(cvUploadedAt)}` : cvLink ? `Previously uploaded · ${formatUploadDate(cvUploadedAt) || "date unavailable"}` : "PDF or Word, up to 5 MB."}</span>
                                    <input id="profile-cv-file" type="file" accept=".pdf,.doc,.docx" hidden onChange={chooseCv} />
                                    <label className="profile-button secondary" htmlFor="profile-cv-file">{cvFile || cvLink ? "Replace file" : "Choose file"}</label>
                                </div>
                            </div>
                            <button className="profile-button primary" type="submit" disabled={saving}>{saving ? "Saving..." : "Save and continue"}</button>
                        </form>
                        <div className="profile-discovery">
                            <div><strong>Be Discovered</strong><small>{discovered ? "On. Employers can find your profile and CV." : "Off. Only employers you apply to can see your profile and CV."}</small></div>
                            <button className={`profile-switch${discovered ? " on" : ""}`} type="button" role="switch" aria-checked={discovered} aria-label="Be Discovered" onClick={() => setDiscovered((value) => !value)} />
                        </div>
                    </div>
                )}
            </main>
            <footer className="profile-footer">
                <div className="profile-footer-inner">
                    <div className="profile-footer-brand">
                        <strong>Samaggi Samagom</strong>
                        <span>, CFAS, the career and SACC platform</span>
                    </div>
                    <div className="profile-footer-links">
                        <a href="mailto:careers@samaggisamagom.com">careers@samaggisamagom.com</a>
                        <a href="/privacy">Privacy</a>
                        <a href="/help">Help</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default ProfilePage;
